import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const ProductPage = () => {
  useEffect(() => {
    document.title = 'Platform - NEXUS LBE';
  }, []);

  const rooms = [
    {
      number: 1,
      name: 'Foyer Fair',
      phase: 'Arrival',
      description: 'Tickets, scene-setting, worldbuild. Heavily dressed social space with photo moments, F&B, merch. Self-guided.',
      image: '/images/journey-1-v3.jpeg',
      features: ['Ticketing & bag check', 'Food & beverage', 'Merchandise', 'Photo opportunities']
    },
    {
      number: 2,
      name: 'Story Briefing',
      phase: 'Briefing',
      description: 'Immersive worldbuild with live actors and high energy. Sets narrative with bespoke AV content.',
      image: '/images/journey-2-v2.jpg',
      features: ['Live actor guides', 'Immersive screens & audio', 'Narrative setup', 'Group rally']
    },
    {
      number: 3,
      name: 'Experience Rooms',
      phase: 'Challenge',
      description: 'Individual puzzles, group screens, interactive challenges. Rotated stations with actor manipulation.',
      image: '/images/journey-3-v2.jpg',
      features: ['Puzzle solving', 'Team collaboration', 'Actor-guided play', 'Progressive difficulty']
    },
    {
      number: 4,
      name: 'Bridge Room',
      phase: 'Transition',
      description: 'Slow down, transition to new world. No actors/watching. Sets up climax.',
      image: '/images/journey-4-v3.jpeg',
      features: ['Atmospheric pacing', 'Sensory transition', 'Anticipation building', 'Moment of reflection']
    },
    {
      number: 5,
      name: 'Triumph',
      phase: 'Climax',
      description: 'High-energy final challenge with surprise ending. Meet heroes, bespoke creative tech element.',
      image: '/images/journey-5-v2.jpg',
      features: ['Interactive finale', 'Story resolution', 'Peak emotional moment', 'IP-specific tech']
    },
    {
      number: 6,
      name: 'Celebration & Share',
      phase: 'Share',
      description: 'Return to Foyer Fair. Cafe, merch, photo booths, social zone for amplifying virality.',
      image: '/images/journey-6-v2.jpg',
      features: ['Social sharing', 'Extended merchandise', 'Refreshments', 'UGC capture']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center">
            <Badge variant="outline" className="mb-4">Our Platform</Badge>
            <h1 className="font-['Geist'] text-4xl md:text-5xl lg:text-6xl mb-6">
              The <span className="text-primary">Formatted</span> LBE Experience
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A standardized 6-room journey delivering consistent, high-quality immersive experiences across all locations.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <h3 className="font-['Geist'] text-xl">80% System, 100% Magic</h3>
                <p className="text-sm text-muted-foreground">
                  80% standardized platform core ensures efficiency and scalability. 
                  20% IP-specific customization delivers 100% unique magic.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <h3 className="font-['Geist'] text-xl">5-Phase Psychology</h3>
                <p className="text-sm text-muted-foreground">
                  Arrival → Briefing → Challenge → Climax → Share. Proven emotional arc 
                  drives retention via peak-end rule.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <h3 className="font-['Geist'] text-xl">Actor-Guided Format</h3>
                <p className="text-sm text-muted-foreground">
                  Live actors guide 12-person groups through 90-minute structured experiences with dynamic storytelling.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Journey */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-12">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              The 6-Room Journey
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Each experience follows our proven format, with rooms designed to create emotional peaks and memorable moments.
            </p>
          </div>
          
          <div className="space-y-8">
            {rooms.map((room, idx) => (
              <div key={room.number} className="grid lg:grid-cols-2 gap-8 items-center">
                <div className={idx % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{room.phase}</Badge>
                      <span className="text-primary font-bold">Room {room.number}</span>
                    </div>
                    <h3 className="font-['Geist'] text-2xl">{room.name}</h3>
                    <p className="text-muted-foreground">{room.description}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {room.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="relative rounded-xl overflow-hidden aspect-[4/3] shadow-lg">
                    <img 
                      src={room.image}
                      alt={`${room.name} experience room`}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4 text-white">
            See it in action
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Discuss how our platform can bring your IP or property vision to life.
          </p>
          <div className="flex flex-col items-center gap-4">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]"
              >
                Get in Touch
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;